import React, { useEffect, useRef, useState } from "react";
import Button from "../../Buttons/Button";
import Modal from "../../Modal/Modal";
import { showToast } from "../../Toast/Toast";

const sizeClasses = {
  xs: "p-1",
  sm: "p-1",
  md: "p-1.5",
  lg: "p-2",
  xl: "p-2",
};

const fontSizeClasses = {
  xs: "text-[12px]",
  sm: "text-[12px]",
  md: "text-[14px]",
  lg: "text-[16px]",
  xl: "text-[18px]",
};

const CustomEmail = ({
  size = "md",
  value = [],
  onChange,
  multiple = false,
  placeholder = "Enter email",
  rounded = "rounded",
  disabled = false,
  className,
  maxLength = 20,
  shadow = false,
  autocomplete = "",
}) => {
  const [emails, setEmails] = useState(value || []);
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  useEffect(() => {
    if (value.length > 0) {
      setEmails(value);
    }
  }, [value]);
  // Email validation regex
  // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // const emailRegex =
  //   /^(?!\.)(?!.*\.\.)[a-zA-Z0-9._%+-]+(?<!\.)@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,6}$/;
  // const emailRegex = /^(?!\.)(?!.*\.\.)[a-zA-Z0-9._%+-]+(?<!\.)@(?:(?!xn--)[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,6}$/;
  const emailRegex =
    /^(?!\.)(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,6}$/;

  const validateEmail = (email) => {
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    if (!inputValue.trim()) return;

    if (!validateEmail(inputValue)) {
      //setError(`"${inputValue}" is not a valid email address`);
      showToast(`"${inputValue}" is not a valid email address`, "error");
      return;
    }

    if (emails.includes(inputValue)) {
      //setError(`"${inputValue}" has already been added`);
      showToast(`"${inputValue}" has already been added`, "warning");
      return;
    }

    const newEmails = [...emails, inputValue];
    setEmails(newEmails);
    setInputValue("");
    setError("");

    if (onChange) {
      onChange(newEmails);
    }
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    // Add email on Enter
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;
    e.preventDefault();

    // Get pasted text and split by newlines, commas, or spaces
    const pasteText = e.clipboardData.getData("text");
    // Split by newlines, commas, or multiple spaces
    const pastedEmails = pasteText
      .split(/[\n\r,;]+/)
      .map((email) => email.trim())
      .filter((email) => email);
    let addedCount = 0;
    let invalidEmails = [];
    let duplicateEmails = [];
    const newEmails = [...emails];

    pastedEmails.forEach((email) => {
      // Skip empty strings
      if (!email) return;

      // Check if valid email
      if (!validateEmail(email)) {
        invalidEmails.push(email);
        return;
      }

      // Check if duplicate
      if (newEmails.includes(email)) {
        duplicateEmails.push(email);
        return;
      }

      newEmails.push(email);
      addedCount++;
    });

    if (addedCount > 0) {
      setEmails(newEmails);
      setInputValue("");

      if (onChange) {
        onChange(newEmails);
      }
    }

    // Set error message if there were invalid or duplicate emails
    if (invalidEmails.length > 0 || duplicateEmails.length > 0) {
      let errorMsg = "";
      if (invalidEmails.length > 0) {
        errorMsg += `Invalid email${
          invalidEmails.length > 1 ? "s" : ""
        }: ${invalidEmails.join(", ")}. `;
      }
      if (duplicateEmails.length > 0) {
        errorMsg += `Duplicate email${
          duplicateEmails.length > 1 ? "s" : ""
        }: ${duplicateEmails.join(", ")}.`;
      }
      //setError(errorMsg);
      showToast(errorMsg, "error");
    }
  };

  const handleDeleteEmail = (index) => {
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails);

    if (onChange) {
      onChange(newEmails);
    }
  };

  const handleClearAll = () => {
    setEmails([]);
    setInputValue("");

    if (onChange) {
      onChange([]);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  };

  //console.log("emails", emails);

  return (
    <div className="flex flex-col w-full">
      {multiple ? (
        <div
          className={`flex items-center rounded border focus:outline-none ${
            isFocused ? !disabled && "border-primary outline-none" : ""
          } 
          ${shadow ? "border-gray-50 inputShadow" : "border-gray-300"}
          ${
            disabled
              ? "border-neutrals-200 bg-neutrals-200 cursor-not-allowed text-black"
              : "cursor-pointer hover:border-primary focus:border-primary "
          }`}
        >
          <div
            className={`flex-1 relative flex items-center overflow-hidden ${
              sizeClasses[size]
            } ${fontSizeClasses[size]} ${className} ${
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
            onClick={handleClick}
            onFocus={handleFocus}
            onBlur={handleBlur}
            tabIndex="0"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex-1 overflow-hidden gap-1 flex items-center justify-start ">
                {emails.length >= 1 && (
                  <div className="line-clamp-1">{emails.join(", ")}</div>
                )}

                <div
                  className={`flex ${
                    emails.length >= 1
                      ? "min-w-20 overflow-hidden flex-auto"
                      : "flex-1"
                  }`}
                >
                  <input
                    ref={inputRef}
                    type="text"
                    className={`outline-none w-full ${
                      disabled ? "cursor-not-allowed" : "cursor-pointer"
                    }`}
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      setError("");
                      // Remove the setEmails call from here for single email mode
                    }}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={emails.length === 0 ? placeholder : "Add more"}
                    disabled={disabled}
                    autoComplete={autocomplete}
                  />
                </div>

                {emails.length > 1 && (
                  <div className="text-gray-500 flex-1 text-nowrap ">
                    {emails.length} {emails.length > 1 ? " emails" : " email"}
                  </div>
                )}
              </div>
            </div>
          </div>
          {emails.length > 0 && (
            <div className="px-2">
              <Button
                type="icon"
                icon={"close"}
                size="xs"
                color="dark"
                variant="text"
                onClick={handleClearAll}
                disabled={disabled}
              />
              {multiple && (
                <Button
                  type="icon"
                  icon={"list"}
                  size="xs"
                  color="dark"
                  variant="text"
                  onClick={() => setIsOpenModal(true)}
                  disabled={disabled}
                />
              )}
            </div>
          )}
        </div>
      ) : (
        <input
          type="email"
          value={value}
          placeholder={placeholder}
          // className={`w-full bg-white border border-gray-300 hover:border-primary focus:border-primary focus:outline-none ${sizeClasses[size]} ${fontSizeClasses[size]} ${rounded}`}
          className={`w-full border border-gray-300 focus:outline-none ${
            sizeClasses[size]
          } ${fontSizeClasses[size]} ${rounded} ${
            disabled
              ? "bg-neutrals-200 cursor-not-allowed text-black"
              : "bg-white hover:border-primary focus:border-primary"
          }`}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      )}
      <Modal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        title="Email Addresses"
        width="max-w-2xl"
      >
        <div className="p-1">
          <div className="flex justify-between mb-4">
            <div>
              <p className="text-gray-500">
                {emails.length} {emails.length === 1 ? "email" : "emails"} added
              </p>
            </div>
            <div className="flex gap-2">
              {/* <Button
                title="Add Email"
                icon="add"
                size="sm"
                onClick={() => {
                  setIsOpenModal(false);
                  setTimeout(() => inputRef.current?.focus(), 100);
                }}
              /> */}
              {emails.length > 0 && (
                <Button
                  title="Clear All"
                  icon="delete"
                  size="sm"
                  color="danger"
                  onClick={handleClearAll}
                />
              )}
            </div>
          </div>

          {emails.length > 0 ? (
            <div className="max-h-[400px] overflow-y-auto grid grid-cols-2 gap-x-4 gap-y-1">
              {emails.map((email, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-1 border-b border-gray-200 last:border-b-0"
                >
                  <div className="flex items-center gap-4">
                    <span className="material-icons text-gray-500 text-sm">
                      email
                    </span>
                    <div>
                      <p className="text-sm">{email}</p>
                    </div>
                  </div>
                  <Button
                    type="icon"
                    icon="delete"
                    size="xs"
                    color="danger"
                    variant="text"
                    onClick={() => handleDeleteEmail(index)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              No email addresses added
            </div>
          )}
          <div
            className={`flex items-center rounded border border-gray-300 hover:border-primary focus:border-primary focus:outline-none ${
              isFocused ? "border-primary outline-none" : ""
            }`}
          >
            <div
              className={`flex-1 relative flex items-center overflow-hidden ${
                sizeClasses[size]
              } ${fontSizeClasses[size]} ${className} ${
                disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }`}
              onClick={handleClick}
              onFocus={handleFocus}
              onBlur={handleBlur}
              tabIndex="0"
            >
              <input
                ref={inputRef}
                type="text"
                className={`outline-none w-full`}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setError("");
                  // Remove the if (!multiple) block here
                }}
                onBlur={(e) => {
                  if (!multiple) {
                    setEmails([inputValue]);
                    if (onChange) {
                      onChange([inputValue]);
                    }
                  } else {
                    handleBlur();
                  }
                }}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onFocus={handleFocus}
                placeholder={emails.length === 0 ? placeholder : "Add more"}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CustomEmail;
